import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from '@parrot-software/design-system';

interface UnsavedChangesDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation dialog shown when the user tries to close a sheet whose form
 * has unsaved changes.
 *
 * "Seguir editando" is the primary (brand-filled) action and keeps the sheet
 * open; "Salir sin guardar" is a neutral secondary action that discards the
 * changes and closes.
 *
 * Close routing is explicit — each callback fires exactly once per dismissal:
 *  1. "Salir sin guardar" (`AlertDialogCancel`) → `onConfirm`. Radix still
 *     closes the dialog natively (`onOpenChange(false)`), but the guard below
 *     suppresses the implicit `onCancel` that close would otherwise trigger.
 *  2. "Seguir editando" (`AlertDialogAction`) → `onCancel`, explicitly via
 *     `onClick` instead of relying on Radix's native close; the guard suppresses
 *     the duplicate call from `onOpenChange(false)`.
 *  3. Any other dismissal (ESC, programmatic close) → `onCancel` through
 *     `handleOpenChange`.
 *
 * Rendered above the domain sheets (overlay `z-[1300]`, content `z-[1350]`):
 * an explicit portaled overlay at `z-[1400]` dims the sheet and the content is
 * raised to `z-[1450]`.
 *
 * @param {UnsavedChangesDialogProps} props - Component properties
 * @returns {React.ReactElement} AlertDialog asking to confirm the close
 */
export const UnsavedChangesDialog = ({ open, onCancel, onConfirm }: UnsavedChangesDialogProps): React.ReactElement => {
  const { t } = useTranslation('subscriptions');
  const dismissalHandledRef = useRef(false);

  const handleConfirm = () => {
    dismissalHandledRef.current = true;
    onConfirm();
  };

  const handleCancel = () => {
    dismissalHandledRef.current = true;
    onCancel();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    if (dismissalHandledRef.current) {
      dismissalHandledRef.current = false;
      return;
    }
    onCancel();
  };

  return (
    <AlertDialog onOpenChange={handleOpenChange} open={open}>
      <AlertDialogPortal>
        <AlertDialogOverlay className="z-[1400] bg-black/50" />
      </AlertDialogPortal>
      <AlertDialogContent className="z-[1450] max-w-[480px] gap-4 rounded-lg p-6 font-sans shadow-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-semibold">{t('unsavedChanges.title')}</AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            {t('unsavedChanges.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex justify-end gap-4">
          <AlertDialogCancel onClick={handleConfirm}>{t('unsavedChanges.confirm')}</AlertDialogCancel>
          <AlertDialogAction onClick={handleCancel}>{t('unsavedChanges.cancel')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
